import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Pie, Bar } from 'react-chartjs-2';
import { Spin, Table as AntTable } from 'antd';
import { Col, Row } from 'antd';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  Title,
  CategoryScale,
  LinearScale,
  BarElement,
} from 'chart.js';
import { useGlobalPerformance } from '../hooks/usePerformance';
import { PerformanceFilterInput, GlobalPerformanceItem } from '../types/performance.types';
import { createGlobalVendorsColumns, VendorWithColor } from './GlobalVendorsCommonTable';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  Title,
  CategoryScale,
  LinearScale,
  BarElement
);

const pieOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'top' as const,
      display: false,
    },
    tooltip: {
      bodyFont: {
        size: 16,
      },
      titleFont: {
        size: 18,
      },
      padding: 10,
      callbacks: {
        label: function (tooltipItem: any) {
          return `Total Premium: $${tooltipItem.formattedValue}`;
        },
      },
    },
    title: {
      display: false,
      text: 'Distribution of Categories',
    },
  },
  elements: {
    arc: {
      borderWidth: 2,
      borderAlign: 'inner' as const,
      borderRadius: 5,
      hoverOffset: 15,
    },
  },
  cutout: '50%',
};

const barOptions = {
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    x: {
      beginAtZero: true,
      grid: {
        display: false,
      },
    },
    y: {
      beginAtZero: true,
      grid: {
        display: false,
      },
    },
  },
  plugins: {
    legend: {
      display: false,
    },
    title: {
      display: true,
      text: 'Average Call Length (s)',
      color: '#000000',
    },
  },
};

const backgroundColors = [
  '#95A4FC',
  '#A1E3CB',
  '#BAEDBD',
  '#B1E3FF',
  '#A8C5DA',
  '#8BC34A',
  '#FFE999',
  '#C6C7F8',
  '#C3A6C6',
  '#FFCB83',
  '#FFC107',
  '#FF5722',
  '#9C27B0',
  '#FF9800',
  '#00BCD4',
];

interface GlobalPerformanceTableProps {
  data: GlobalPerformanceItem[];
  loading: boolean;
}

const TopGlobalVendorsChart: React.FC<GlobalPerformanceTableProps> = ({ data, loading }) => {
  const [chartData, setChartData] = React.useState({
    labels: [] as string[],
    datasets: [{ data: [] as number[], backgroundColor: [] as string[] }],
  });
  const [barData, setBarData] = React.useState({
    labels: [] as string[],
    datasets: [{ data: [] as number[], backgroundColor: [] as string[] }],
  });
  const [topVendorsData, setTopVendorsData] = React.useState<VendorWithColor[]>([]);

  useEffect(() => {
    if (data && data.length > 0) {
      // First, aggregate duplicates by globalName
      const aggregatedData = data.reduce((acc: any[], item: any) => {
        if (!item.globalName || item.globalName.trim() === '') {
          return acc;
        }

        const globalName = item.globalName.trim();
        const existingIndex = acc.findIndex((existing) => existing.globalName === globalName);

        if (existingIndex >= 0) {
          const existing = acc[existingIndex];
          acc[existingIndex] = {
            ...existing,
            totalLeads: (existing.totalLeads || 0) + (item.totalLeads || 0),
            totalBillableLeads: (existing.totalBillableLeads || 0) + (item.totalBillableLeads || 0),
            totalCost: (existing.totalCost || 0) + (item.totalCost || 0),
            totalACASales: (existing.totalACASales || 0) + (item.totalACASales || 0),
            totalU65Sales: (existing.totalU65Sales || 0) + (item.totalU65Sales || 0),
            totalPrivateMedPremium: (existing.totalPrivateMedPremium || 0) + (item.totalPrivateMedPremium || 0),
            totalAncillaries: (existing.totalAncillaries || 0) + (item.totalAncillaries || 0),
            totalAncillariesPremium: (existing.totalAncillariesPremium || 0) + (item.totalAncillariesPremium || 0),
            totalCallLength: (existing.totalCallLength || 0) + (item.totalCallLength || 0),
          };
        } else {
          acc.push({ ...item, globalName });
        }

        return acc;
      }, []);

      // Get top 10 vendors by premium from aggregated data
      const topVendors: VendorWithColor[] = aggregatedData
        .sort((a, b) => {
          const premiumA = (a.totalPrivateMedPremium || 0) + (a.totalAncillariesPremium || 0);
          const premiumB = (b.totalPrivateMedPremium || 0) + (b.totalAncillariesPremium || 0);
          return premiumB - premiumA;
        })
        .slice(0, 10)
        .map((item, index) => ({
          ...item,
          color: backgroundColors[index % backgroundColors.length],
        }));

      setChartData({
        labels: topVendors.map((item) => item.globalName),
        datasets: [
          {
            data: topVendors.map((item) => (item.totalPrivateMedPremium || 0) + (item.totalAncillariesPremium || 0)),
            backgroundColor: topVendors.map((item) => item.color || '#95A4FC'),
          },
        ],
      });
      setBarData({
        labels: topVendors.map((item) => item.globalName),
        datasets: [
          {
            data: topVendors.map((item) => {
              const totalBillableLeads = item.totalBillableLeads || 0;
              return totalBillableLeads > 0 ? Math.round((item.totalCallLength || 0) / totalBillableLeads) : 0;
            }),
            backgroundColor: topVendors.map((item) => item.color || '#95A4FC'),
          },
        ],
      });
      setTopVendorsData(topVendors);
    }
  }, [data]);

  const columns = createGlobalVendorsColumns(true);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin />
      </div>
    );
  }

  return (
    <>
      <Row gutter={[24, 24]}>
        <Col xs={24} sm={24} md={12} lg={12}>
          <div style={{ height: '500px' }}>
            <Bar data={barData} options={barOptions} />
          </div>
        </Col>
        <Col xs={24} sm={24} md={12} lg={12}>
          <div style={{ height: '500px' }} >
            <h3>Top 10 Vendors by Premium</h3>
            <Pie data={chartData} options={pieOptions} />
          </div>
        </Col>
      </Row>
      <Row gutter={[24, 24]} style={{ marginTop: '68px' }}>
        <Col span={24}>
          <AntTable
            columns={columns}
            dataSource={topVendorsData}
            rowKey={(record) => record.globalName}
            pagination={false}
            size="small"
          />
        </Col>
      </Row>
    </>
  );
};

interface TopGlobalVendorsProps {}

const TopGlobalVendors: React.FC<TopGlobalVendorsProps> = () => {
  const startDate = useSelector((state: any) => state.dropDown?.startDate);
  const endDate = useSelector((state: any) => state.dropDown?.endDate);
  const { data, loading, error, refetch } = useGlobalPerformance();

  useEffect(() => {
    const filter: PerformanceFilterInput = {};

    if (startDate) filter.startDate = startDate;
    if (endDate) filter.endDate = endDate;

    refetch(filter);
  }, [startDate, endDate, refetch]);

  if (error) {
    return (
      <div style={{ color: 'red', padding: '20px' }}>
        Error loading data: {error.message}
      </div>
    );
  }

  return <TopGlobalVendorsChart data={data} loading={loading} />;
};

export default TopGlobalVendors;
